import cuid from 'cuid';

import parseFileName from './parseFileName';

/**
 * Single source of truth for the Step 4 bulk-upload file list.
 *
 * Every mutation is an action applied to the latest state, so concurrent writers — the
 * batch drop loop and many in-flight uploads reporting completion/error — can never
 * clobber one another off a stale snapshot. This is the fix for COR-3192, where the old
 * class component managed the list with non-functional `setState` on stale copies and
 * silently dropped files that were never enqueued.
 *
 * Entries are addressed by a stable `id` (never array index): a delete/abort splice must not
 * cause an in-flight upload to mark or remove the wrong file.
 */

export const ADD_FILES = 'ADD_FILES';
export const MARK_COMPLETE = 'MARK_COMPLETE';
export const MARK_ERROR = 'MARK_ERROR';
export const ABORT = 'ABORT';
export const UPDATE = 'UPDATE';
export const CLEAR_UPDATED = 'CLEAR_UPDATED';
export const DELETE = 'DELETE';
export const DELETE_ERRORS = 'DELETE_ERRORS';
export const ADMIT_NEXT = 'ADMIT_NEXT';

export const initialFilesState = { files: [] };

/**
 * Build a normalized entry. `file` is the raw File/Blob; its metadata lives alongside it so
 * spreading an entry to update a flag never clones the Blob.
 */
export const createFileEntry = (file, overrides = {}) => ({
  id: cuid(),
  file,
  name: file.name,
  isComplete: false,
  hasError: false,
  error: {},
  photoID: null,
  isSelected: false,
  isUpdatedFile: false,
  admitted: false,
  ...overrides
});

const setById = (files, id, patch) => files.map(f => (f.id === id ? { ...f, ...patch } : f));

const step4FilesReducer = (state, action) => {
  switch (action.type) {
    /**
     * Append incoming entries, replacing any existing entry that collides by parsed file name
     * (7000.1.jpg / 7000_1.jpg / 7000-1.jpg are the same file). Purely additive otherwise —
     * it cannot drop a tail.
     */
    case ADD_FILES: {
      const incomingKeys = new Set(action.entries.map(e => parseFileName(e.name)));
      const kept = state.files.filter(f => !incomingKeys.has(parseFileName(f.name)));
      return { ...state, files: [...kept, ...action.entries] };
    }

    case MARK_COMPLETE:
      return {
        ...state,
        files: setById(state.files, action.id, { isComplete: true, photoID: action.photoID })
      };

    // `error` is the resolved imageUploadErrors(...) object; the caller owns the code→copy mapping.
    case MARK_ERROR:
      return {
        ...state,
        files: setById(state.files, action.id, {
          isComplete: true,
          hasError: true,
          error: action.error
        })
      };

    // Edit/replace: swap the underlying file and re-run its upload.
    case UPDATE:
      return {
        ...state,
        files: setById(state.files, action.id, {
          file: action.file,
          name: action.file.name,
          isComplete: false,
          hasError: false,
          error: {},
          isUpdatedFile: true,
          admitted: true
        })
      };

    case CLEAR_UPDATED:
      return { ...state, files: setById(state.files, action.id, { isUpdatedFile: false }) };

    // Abort (never-uploaded errored file) and delete (uploaded file, after CPS delete) both
    // remove by id.
    case ABORT:
    case DELETE:
      return { ...state, files: state.files.filter(f => f.id !== action.id) };

    case DELETE_ERRORS:
      return { ...state, files: state.files.filter(f => !f.hasError) };

    case ADMIT_NEXT: {
      const count = action.count || 0;
      if (count <= 0) return state;
      let admittedCount = 0;
      const files = state.files.map(f => {
        if (admittedCount < count && !f.admitted && !f.isComplete) {
          admittedCount += 1;
          return { ...f, admitted: true };
        }
        return f;
      });
      return { ...state, files };
    }

    default:
      return state;
  }
};

export default step4FilesReducer;
